import * as z from 'zod'
import { toast } from 'sonner'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRef, useEffect } from 'react'
import { uploadFile } from '@/lib/firebase'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import { useBoolean } from '@/hooks/use-boolean'
import { zodResolver } from '@hookform/resolvers/zod'
import { Separator } from '@/components/ui/separator'
import { useRouter } from '@/routes/hooks/use-router'
import { User, Role, ShopStatus } from '@/types/user.type'
import RichTextEditor from '@/components/ui/rich-text-editor'
import { User as UserIcon, Store, Save, X, ImagePlus } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// Form validation schema
const profileFormSchema = z.object({
  displayName: z.string().min(1, 'กรุณากรอกชื่อ'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phoneNumber: z.string().optional(),
  lineId: z.string().optional(),
  imageUrl: z.string().optional(),
  shopDescription: z.string().optional(),
  shopStatus: z.nativeEnum(ShopStatus).optional(),
  shopCoverUrl: z.string().optional(),
  shopBannerUrl: z.string().optional(),
})

type ProfileFormData = z.infer<typeof profileFormSchema>

interface BannerImageUploadProps {
  label: string
  value: File | null
  previewUrl: string | null
  fileName?: string
  onChange: (
    file: File | null,
    previewUrl: string | null,
    fileName?: string,
  ) => void
  width?: string
  height?: string
  aspectRatio?: string
  isCircular?: boolean
}

function BannerImageUpload({
  label,
  value,
  previewUrl,
  fileName,
  onChange,
  width = 'w-full',
  height = 'h-44',
  aspectRatio,
  isCircular = false,
}: BannerImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [imageLoading, setImageLoading] = useState(false)
  const [imageError, setImageError] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('กรุณาเลือกรูปภาพเท่านั้น')
        return
      }
      const url = URL.createObjectURL(file)
      onChange(file, url, file.name)
      setImageError(false)
    }
  }

  const handleRemove = () => {
    onChange(null, null, undefined)
    setImageError(false)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleImageLoad = () => {
    setImageLoading(false)
    setImageError(false)
  }

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error(`Failed to load image: ${previewUrl}`)
    setImageLoading(false)
    setImageError(true)
    e.currentTarget.style.display = 'none'
  }

  return (
    <div className="flex flex-col w-full gap-2">
      <label className="font-medium">{label}</label>
      <div className="flex flex-col items-center gap-4">
        <div
          className={`relative ${width} ${aspectRatio ? aspectRatio : height} border border-dashed border-muted-foreground ${
            isCircular ? 'rounded-full' : 'rounded-md'
          } flex items-center justify-center bg-muted`}
        >
          {previewUrl && !imageError ? (
            <>
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-20">
                  <div className="text-xs text-muted-foreground">
                    กำลังโหลด...
                  </div>
                </div>
              )}
              <img
                src={previewUrl}
                alt="banner preview"
                className={`object-cover w-full h-full ${
                  isCircular ? 'rounded-full' : 'rounded-md'
                } ${aspectRatio ? aspectRatio : height}`}
                onLoad={handleImageLoad}
                onError={handleImageError}
                onLoadStart={() => setImageLoading(true)}
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="absolute top-1 right-1 z-10 bg-gray-100/50 hover:bg-gray-100/70"
                onClick={handleRemove}
              >
                <X className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="ghost"
              className={`flex flex-col items-center justify-center w-full h-full ${
                isCircular ? 'rounded-full' : ''
              }`}
              onClick={() => inputRef.current?.click()}
            >
              <ImagePlus className="w-8 h-8 mb-1 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">
                {previewUrl && imageError ? 'รูปภาพเสียหาย' : 'เลือกรูปภาพ'}
              </span>
            </Button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
        {(value || fileName) && (
          <div>
            <span className="text-xs text-muted-foreground">
              {fileName || (value ? value.name : '')}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

interface ProfileFormProps {
  user: User | null
  onCancel?: () => void
}

export default function ProfileForm({ user, onCancel }: ProfileFormProps) {
  const router = useRouter()
  const reload = useBoolean()
  const isSubmitting = useBoolean(false)

  const [profileImage, setProfileImage] = useState<{
    file: File | null
    previewUrl: string | null
    fileName?: string
  }>({
    file: null,
    previewUrl: user?.imageUrl || null,
  })
  const [shopCoverImage, setShopCoverImage] = useState<{
    file: File | null
    previewUrl: string | null
    fileName?: string
  }>({
    file: null,
    previewUrl: user?.shopCoverUrl || null,
  })
  const [shopBannerImage, setShopBannerImage] = useState<{
    file: File | null
    previewUrl: string | null
    fileName?: string
  }>({
    file: null,
    previewUrl: user?.shopBannerUrl || null,
  })

  // Update image states when user data changes
  useEffect(() => {
    if (user) {
      setProfileImage((prev) => ({
        ...prev,
        previewUrl: user.imageUrl || null,
      }))
      setShopCoverImage((prev) => ({
        ...prev,
        previewUrl: user.shopCoverUrl || null,
      }))
      setShopBannerImage((prev) => ({
        ...prev,
        previewUrl: user.shopBannerUrl || null,
      }))
    }
  }, [user])

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      displayName: user?.displayName || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.phoneNumber || '',
      lineId: user?.lineId || '',
      imageUrl: user?.imageUrl || '',
      shopDescription: user?.shopDescription || '',
      shopStatus: user?.shopStatus,
      shopCoverUrl: user?.shopCoverUrl || '',
      shopBannerUrl: user?.shopBannerUrl || '',
    },
  })

  const onSubmit = async (data: ProfileFormData): Promise<void> => {
    try {
      isSubmitting.onTrue()
      const finalData: ProfileFormData = { ...data }

      // Upload profile image if selected
      if (profileImage.file) {
        toast.info('กำลังอัปโหลดรูปภาพ...')
        const imageUrl: string = await uploadImageToStorage(profileImage.file)
        finalData.imageUrl = imageUrl
        setProfileImage((prev) => ({
          ...prev,
          file: null,
          previewUrl: imageUrl,
        }))
        toast.success('อัปโหลดรูปภาพสำเร็จ')
      }

      // Upload shop cover if selected
      if (shopCoverImage.file) {
        toast.info('กำลังอัปโหลดรูปปกร้านค้า...')
        const coverUrl: string = await uploadImageToStorage(shopCoverImage.file)
        finalData.shopCoverUrl = coverUrl
        setShopCoverImage((prev) => ({
          ...prev,
          file: null,
          previewUrl: coverUrl,
        }))
        toast.success('อัปโหลดรูปปกร้านค้าสำเร็จ')
      }

      // Upload shop banner if selected
      if (shopBannerImage.file) {
        toast.info('กำลังอัปโหลดรูปแบนเนอร์...')
        const bannerUrl: string = await uploadImageToStorage(
          shopBannerImage.file,
        )
        finalData.shopBannerUrl = bannerUrl
        setShopBannerImage((prev) => ({
          ...prev,
          file: null,
          previewUrl: bannerUrl,
        }))
        toast.success('อัปโหลดรูปแบนเนอร์สำเร็จ')
      }

      // Remove fields with empty string values
      ;(Object.keys(finalData) as Array<keyof ProfileFormData>).forEach(
        (key) => {
          if (finalData[key] === '' || finalData[key] === undefined) {
            delete finalData[key]
          }
        },
      )

      // Default save logic if no onSave provided

      await axios.patch(endpoints.profile.update, finalData)
      toast.success('บันทึกข้อมูลสำเร็จ')

      router.back()
    } catch (error) {
      toast.error('เกิดข้อผิดพลาดในการบันทึกข้อมูล')
      console.error('Profile save error:', error)
    } finally {
      reload.onToggle()
      isSubmitting.onTrue()
    }
  }

  const handleCancel = () => {
    form.reset()
    setProfileImage({
      file: null,
      previewUrl: user?.imageUrl || null,
    })
    setShopCoverImage({
      file: null,
      previewUrl: user?.shopCoverUrl || null,
    })
    setShopBannerImage({
      file: null,
      previewUrl: user?.shopBannerUrl || null,
    })
    onCancel?.()
  }

  const handleProfileImageChange = (
    file: File | null,
    previewUrl: string | null,
    fileName?: string,
  ) => {
    setProfileImage({ file, previewUrl, fileName })
    if (previewUrl) {
      form.setValue('imageUrl', previewUrl)
    }
  }

  const handleShopCoverChange = (
    file: File | null,
    previewUrl: string | null,
    fileName?: string,
  ) => {
    setShopCoverImage({ file, previewUrl, fileName })
    if (previewUrl) {
      form.setValue('shopCoverUrl', previewUrl)
    }
  }

  const handleShopBannerChange = (
    file: File | null,
    previewUrl: string | null,
    fileName?: string,
  ) => {
    setShopBannerImage({ file, previewUrl, fileName })
    if (previewUrl) {
      form.setValue('shopBannerUrl', previewUrl)
    }
  }

  const uploadImageToStorage = async (file: File): Promise<string> => {
    const fileName = `profile_${Date.now()}_${file.name}`
    return await uploadFile(file, 'profile-images', fileName)
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">ไม่พบข้อมูลผู้ใช้</h2>
          <p className="text-muted-foreground">กรุณาเข้าสู่ระบบใหม่</p>
        </div>
      </div>
    )
  }

  const getShopStatusLabel = (status: ShopStatus) => {
    switch (status) {
      case ShopStatus.OPEN:
        return 'เปิดทำการ'
      case ShopStatus.CLOSED:
        return 'ปิดทำการ'
      default:
        return status
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            ข้อมูลส่วนตัว
          </CardTitle>
          <CardDescription>แก้ไขข้อมูลพื้นฐานของบัญชีผู้ใช้</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-3">
                <BannerImageUpload
                  label="รูปโปรไฟล์"
                  value={profileImage.file}
                  previewUrl={profileImage.previewUrl}
                  fileName={profileImage.fileName}
                  onChange={handleProfileImageChange}
                  width="w-32"
                  height="h-32"
                  isCircular={true}
                />
              </div>
              <div className="col-span-9 space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="displayName">ชื่อที่แสดง *</Label>
                    <Input
                      id="displayName"
                      {...form.register('displayName')}
                      placeholder="กรอกชื่อที่แสดง"
                    />
                    {form.formState.errors.displayName && (
                      <p className="text-sm text-red-500">
                        {form.formState.errors.displayName.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">อีเมล</Label>
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ''}
                      disabled={true}
                      placeholder="อีเมล"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="firstName">ชื่อ</Label>
                    <Input
                      id="firstName"
                      {...form.register('firstName')}
                      placeholder="กรอกชื่อ"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">นามสกุล</Label>
                    <Input
                      id="lastName"
                      {...form.register('lastName')}
                      placeholder="กรอกนามสกุล"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">เบอร์โทรศัพท์</Label>
                    <Input
                      id="phoneNumber"
                      {...form.register('phoneNumber')}
                      placeholder="กรอกเบอร์โทรศัพท์"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lineId">Line ID</Label>
                    <Input
                      id="lineId"
                      {...form.register('lineId')}
                      placeholder="กรอก Line ID"
                    />
                  </div>
                </div>
              </div>
            </div>
            {user.role === Role.SHOP && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Store className="h-5 w-5" />
                    <h4 className="text-lg font-semibold">ข้อมูลร้านค้า</h4>
                  </div>
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12 grid grid-cols-2 gap-6">
                      {/* Shop Cover Image */}
                      <BannerImageUpload
                        label="รูปปกร้านค้า"
                        value={shopCoverImage.file}
                        previewUrl={shopCoverImage.previewUrl}
                        fileName={shopCoverImage.fileName}
                        onChange={handleShopCoverChange}
                        width="w-full"
                        height="h-32"
                      />

                      {/* Shop Banner Image */}
                      <BannerImageUpload
                        label="รูปแบนเนอร์ร้านค้า"
                        value={shopBannerImage.file}
                        previewUrl={shopBannerImage.previewUrl}
                        fileName={shopBannerImage.fileName}
                        onChange={handleShopBannerChange}
                        width="w-full"
                        height="h-32"
                      />
                    </div>
                    <div className="col-span-9 space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="shopStatus">สถานะร้านค้า</Label>
                        <Select
                          value={form.watch('shopStatus')}
                          onValueChange={(value) =>
                            form.setValue('shopStatus', value as ShopStatus)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="เลือกสถานะร้านค้า" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={ShopStatus.OPEN}>
                              {getShopStatusLabel(ShopStatus.OPEN)}
                            </SelectItem>
                            <SelectItem value={ShopStatus.CLOSED}>
                              {getShopStatusLabel(ShopStatus.CLOSED)}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="shopDescription">คำอธิบายร้านค้า</Label>
                        <RichTextEditor
                          value={form.watch('shopDescription') || ''}
                          onChange={(value) =>
                            form.setValue('shopDescription', value)
                          }
                          placeholder="กรอกคำอธิบายร้านค้า"
                          className="min-h-[150px]"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSubmitting.value}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                ยกเลิก
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting.value}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {isSubmitting.value ? 'กำลังบันทึก...' : 'บันทึก'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
